import { useState, useEffect } from 'react';
import { X, Building } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Copro {
  id: string;
  numero: string;
  residence: string;
}

interface CoproModalProps {
  copro?: Copro | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tableName?: string;
}

export default function CoproModal({ copro, isOpen, onClose, onSuccess, tableName = 'copros' }: CoproModalProps) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    numero: '',
    residence: ''
  });

  useEffect(() => {
    if (copro) {
      setFormData({
        numero: copro.numero,
        residence: copro.residence
      });
    } else {
      setFormData({
        numero: '',
        residence: ''
      });
    }
  }, [copro]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!formData.numero.trim()) {
      setError('Le numéro de copropriété est requis');
      setLoading(false);
      return;
    }

    try {
      if (copro) {
        // Mode modification
        const { error: updateError } = await supabase
          .from(tableName)
          .update({
            numero: formData.numero.trim(),
            residence: formData.residence.trim()
          })
          .eq('id', copro.id);

        if (updateError) throw updateError;
      } else {
        // Mode création
        const { error: createError } = await supabase
          .from(tableName)
          .insert([{
            numero: formData.numero.trim(),
            residence: formData.residence.trim()
          }]);

        if (createError) throw createError;
      }

      onSuccess();
      onClose();
      setFormData({ numero: '', residence: '' });
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-md relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Building size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">
                {copro ? 'Modifier la copropriété' : 'Nouvelle copropriété'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 p-6 space-y-5">

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Numéro de copropriété</label>
              <input
                type="text"
                value={formData.numero}
                onChange={(e) => setFormData(prev => ({ ...prev, numero: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom de la résidence</label>
              <input
                type="text"
                value={formData.residence}
                onChange={(e) => setFormData(prev => ({ ...prev, residence: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Chargement…' : (copro ? 'Enregistrer' : 'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
